<template>
  <div>
    <PageHeader
      v-if="isAuthenticated"
      :current-app="$store.getters.getCurrentApp"
      :config="headerConfig"
      :user="$store.getters.getUser"
      :app-access="appAccess"
      @logout="handleLogout"
    />
    <SubNav v-if="isAuthenticated" />
    <router-view v-if="isAuthenticated" />
    <div
      v-if="visibleBanner"
      class="alert alert-dark text-center mb-0 banner browserWarning"
      role="alert"
    >
      OBS: Verktyget är optimerat för Chrome browser
      NOTE: This tool is optimized for Chrome browser

      <button
        type="button"
        class="btn btn-primary btn-sm ms-3 browserBannerBtn"
        @click="hideBrowserBanner()"
      >
        OK
      </button>
    </div>
  </div>
</template>

<script>
import { PageHeader } from '@mms/component-library';
import { getAppAccessFromApplications, clearSessionCookies, loadCachedAppAccess, saveAppAccess } from '@mms/shared-session';
import { saveSharedConfig, loadCachedSharedConfig } from '@mms/shared-settings';
import SubNav from "./SubNav/SubNav";
import { fetchFormFieldData } from "../../api/api.js";
import globalConfig from "@/mixins/globalConfig";

export default {
  name: "Layout",
  components: {
    SubNav,
    PageHeader
  },
  mixins: [globalConfig],
  data() {
    return {
      visibleBanner: false,
      isAuthenticated: false,
      appAccess: (() => {
        const cached = loadCachedAppAccess();
        return cached
          ? { ...cached, broadcast: true, 'video-in-home': true }
          : { broadcast: true, 'online-video': false, 'cross-platform': false, 'video-in-home': true, portal: true };
      })()
    };
  },
  computed: {
    headerConfig() {
      const cached = this.config_BackgroundColor ? null : loadCachedSharedConfig();
      return {
        backgroundColor: this.config_BackgroundColor || cached?.BackgroundColor || '',
        heading: this.$store.getters.getCurrentApp === 'video-in-home'
          ? (this.config_ApplicationHeadingVideoInHome || cached?.ApplicationHeadingVideoInHome || '')
          : (this.config_ApplicationHeading || cached?.ApplicationHeading || ''),
        subHeading: this.config_ApplicationSubHeading || cached?.ApplicationSubHeading || '',
        subHeadingVideoInHome: this.config_ApplicationSubHeadingVideoInHome || cached?.ApplicationSubHeadingVideoInHome || '',
        authUrl: this.config_WebCaatAuthWebUrl || cached?.WebCaatAuthWebUrl || '',
        appLinks: {
          broadcast: this.config_LinkTvAnalys || cached?.LinkTvAnalys || '',
          'online-video': this.config_LinkOnlineAnalys || cached?.LinkOnlineAnalys || '',
          'cross-platform': this.config_LinkTotalanalys || cached?.LinkTotalanalys || '',
          'video-in-home': this.config_LinkVideoInHome || cached?.LinkVideoInHome || '',
          portal: this.config_LinkDataPortal || cached?.LinkDataPortal || ''
        }
      };
    }
  },
  watch: {
    '$store.state.config.runtime'(runtime) {
      if (runtime) saveSharedConfig(runtime);
    }
  },
  async mounted() {
    this.initializeBanner();
    await this.loadGlobalConfigFromFile();
  },
  methods: {
    initializeBanner() {
      if (
        localStorage.getItem("browserBanner") == null &&
        !this.$browserDetect.isChrome
      )
        this.visibleBanner = true;
    },
    hideBrowserBanner() {
      localStorage.setItem("browserBanner", true);
      this.visibleBanner = false;
    },
    toastMessage(message, closeButton) {
      if (closeButton) {
        const h = this.$createElement;

        const vNodesMsg = h("b", { class: ["text-center", "mb-0"] }, [
          "Problem fetching default date from the server. The service will not function correctly.",
          h(
            "b-button",
            {
              class: ["float-right", "btn-sm", "mr-3"],
              on: {
                click: () => this.$bvToast.hide("toast")
              }
            },
            "Close"
          )
        ]);

        this.$bvToast.toast([vNodesMsg], {
          id: "toast",
          title: "Warning",
          variant: "danger",
          solid: true,
          noCloseButton: true,
          autoHideDelay: 300000
        });
      } else {
        this.$bvToast.toast(message, {
          title: "Warning",
          variant: "warning",
          solid: true,
          noCloseButton: false
        });
      }
    },
    setApplicationAccess(applications) {
      const access = getAppAccessFromApplications(applications);
      saveAppAccess(access);
      this.appAccess = { ...access, broadcast: true, 'video-in-home': true };
    },
    handleLogout() {
      clearSessionCookies(this.config_AuthCookieDomain);
      window.location.href = this.config_WebCaatAuthWebUrl;
    }
  },
  async created() {
    // Determine the current app based on subdomain
    this.$store.dispatch("determineVideoInHomeSubdomain");

    // Get token and user from URL params, then localStorage, then JWT cookie (robust across portal round-trips)
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    let token = urlParams.get("token");
    let user = urlParams.get("user");

    if (token === null) {
      token = localStorage.getItem("accessToken");
      user = localStorage.getItem("user");
    }

    // Treat stored string "null" as missing (avoid overwriting with null)
    if (user === "null" || user === "") user = null;
    if (token === "null" || token === "") token = null;

    // When user is missing (e.g. after portal), we don't set null; fetchFormFieldData("User")
    // below will repopulate from API (auth cookie is sent with request, works with HttpOnly).

    if (token != null) localStorage.setItem("accessToken", token);
    if (user != null) {
      localStorage.setItem("user", user);
      this.$store.commit("setUser", user);
    }

    this.$store.commit("setLoggedIn", true);
    this.$store.commit("setAccessToken", token);

    try {
      const userData = await fetchFormFieldData("User");
      this.setApplicationAccess(userData.applications);

      this.$store.commit("setInternalAndMMSOnly", userData.internalAndMMSOnly);
      localStorage.setItem("user", userData.email);
      this.$store.commit("setUser", userData.email);

      // Set up user access permissions
      userData.applications.forEach(app => {
        switch (app.applicationName) {
          case "tv-analys-light":
            this.$store.commit("setLightAccess", true);
            break;
          case "tv-analys-annonsör":
            this.$store.commit("setAdvertiserAccess", true);
            break;
          case "tv-analys":
            this.$store.commit("setFullAccess", true);
            break;
          case "mms-användare":
            this.$store.commit("setMmsUser", true);
            break;
          case "video-in-home":
            this.$store.commit("setVideoInHomeAccess", true);
            break;
        }
      });

      // Check permission based on the current app
      let hasAccess = false;

      if (this.$store.getters.getCurrentApp === "video-in-home") {
        // For VIDEO IN HOME app, check videoInHomeAccess
        hasAccess = this.$store.state.systemInfo.videoInHomeAccess;
      } else {
        // For BROADCAST app, check any of the TV analysis access levels
        hasAccess =
          this.$store.state.systemInfo.lightAccess ||
          this.$store.state.systemInfo.advertiserAccess ||
          this.$store.state.systemInfo.fullAccess;
      }

      if (!hasAccess) {
        // Redirect to login if user doesn't have access
        const currentUrl = window.location.href;
        window.location.href =
          this.config_WebCaatAuthWebUrl +
          "?returnUrl=" +
          encodeURIComponent(currentUrl);
      } else {
        // User is authenticated and has access
        this.isAuthenticated = true;
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        this.toastMessage("A problem occurred while fetching the user.", false);
      }
      console.error(error);

      // Redirect to login on error
      const currentUrl = window.location.href;
      window.location.href =
        this.config_WebCaatAuthWebUrl +
        "?returnUrl=" +
        encodeURIComponent(currentUrl);
    }
  }
};
</script>

<style scoped>
.banner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  border-radius: 0;
  background: #424851;
}

.browserWarning {
  color: red;
}

.browserBannerBtn {
  float: right;
  background: #97d058;
  border: none;
}
</style>
